import React, { useRef } from "react"
import { GoogleMap, Polygon, Marker } from "@react-google-maps/api"
import { Button } from "antd"
import Strings from "utils/strings"
import { LatLng } from "utils/geojson"

/**
 * Draw / edit a single polygon on a Google Map and report its path.
 *
 * Click on the map to append vertices; drag a vertex (once there are 3+) to
 * adjust. Uses plain map clicks + an editable Polygon rather than the Maps
 * DrawingManager, which was removed from the Maps JS API as of v3.65.
 *
 * Relies on the app-wide <LoadScript> (see screens/App) having loaded the Maps
 * script, so it renders <GoogleMap> directly without its own loader.
 */
interface ZoneDrawMapProps {
	value: LatLng[]
	onChange: (path: LatLng[]) => void
	center?: LatLng
}

const containerStyle: React.CSSProperties = {
	width: "100%",
	height: "360px",
	borderRadius: 5,
}

const defaultCenter: LatLng = { lat: 38.72152, lng: -9.142161 }

const polygonOptions = {
	fillColor: "#1C70D9",
	fillOpacity: 0.15,
	strokeColor: "#1C70D9",
	strokeWeight: 2.5,
}

const ZoneDrawMap: React.FC<ZoneDrawMapProps> = ({
	value,
	onChange,
	center = defaultCenter,
}) => {
	const polygonRef = useRef<google.maps.Polygon | null>(null)

	const readPath = (polygon: google.maps.Polygon): LatLng[] =>
		polygon
			.getPath()
			.getArray()
			.map((p) => ({ lat: p.lat(), lng: p.lng() }))

	// Append a vertex on map click.
	const handleMapClick = (e: google.maps.MapMouseEvent) => {
		if (!e.latLng) return
		onChange([...value, { lat: e.latLng.lat(), lng: e.latLng.lng() }])
	}

	// Re-read the path after the user drags/edits a vertex.
	const handleEdit = () => {
		if (polygonRef.current) onChange(readPath(polygonRef.current))
	}

	const hasPolygon = value.length >= 3

	return (
		<div>
			<GoogleMap
				mapContainerStyle={containerStyle}
				center={center}
				zoom={12}
				onClick={handleMapClick}
			>
				{hasPolygon && (
					<Polygon
						path={value}
						editable
						draggable={false}
						onLoad={(polygon) => {
							polygonRef.current = polygon
						}}
						onMouseUp={handleEdit}
						options={polygonOptions}
					/>
				)}
				{/* Show the clicked vertices (helpful before the polygon closes). */}
				{value.map((pt, index) => (
					<Marker key={index} position={pt} label={`${index + 1}`} />
				))}
			</GoogleMap>
			<div style={{ marginTop: 8, display: "flex", gap: 8 }}>
				<Button
					size="small"
					disabled={value.length === 0}
					onClick={() => {
						polygonRef.current = null
						onChange([])
					}}
				>
					{Strings.contracts.geojsonRedraw}
				</Button>
				<span style={{ color: "#888", fontSize: 12, alignSelf: "center" }}>
					{Strings.contracts.geojsonDrawHint}
				</span>
			</div>
		</div>
	)
}

export default ZoneDrawMap
